import type { PlaybookId } from "@repo/app-types/entities/playbook";
import {
  fuzzySearchDefaultResultLimit,
  serializeFuzzySearchResourceKey,
  type FuzzySearchResourceKey,
  type FuzzySearchResult,
  type TeamPerformanceSearchResults,
} from "@repo/app-types/entities/search";
import { api } from "../redux.js";

const searchApi = api.injectEndpoints({
  endpoints: (builder) => ({
    fuzzySearch: builder.query<
      FuzzySearchResult[],
      {
        query: string;
        resourceTypes?: readonly string[];
        limit?: number;
      },
      { data: { search_results: FuzzySearchResult[] } }
    >({
      query: ({
        query,
        resourceTypes,
        limit = fuzzySearchDefaultResultLimit,
      }) => ({
        method: "GET",
        url: "/search/fuzzy",
        params: {
          q: query,
          ...(resourceTypes && { resource_types: resourceTypes.join(",") }),
          limit,
        },
      }),
      transformResponse: (response) => response.data.search_results,
      keepUnusedDataFor: 10,
    }),
    teamPerformanceSearch: builder.query<
      TeamPerformanceSearchResults,
      { query: string; playbookId?: PlaybookId; limit?: number },
      { data: TeamPerformanceSearchResults }
    >({
      query: ({ query, playbookId, limit }) => ({
        method: "GET",
        url: "/search/team-performance",
        params: {
          q: query,
          ...(playbookId !== undefined && { playbook_id: playbookId }),
          ...(limit !== undefined && { limit }),
        },
      }),
      transformResponse: (response) => response.data,
      keepUnusedDataFor: 10,
    }),
    searchResources: builder.query<
      FuzzySearchResult[],
      {
        resourceKeys: readonly FuzzySearchResourceKey[];
      },
      { data: { search_results: FuzzySearchResult[] } }
    >({
      query: ({ resourceKeys }) => ({
        method: "GET",
        url: "/search/resources",
        params: {
          keys: resourceKeys.map(serializeFuzzySearchResourceKey).join(","),
        },
      }),
      transformResponse: (response) => response.data.search_results,
      keepUnusedDataFor: 10,
    }),
  }),
});

export const {
  useLazyFuzzySearchQuery,
  useLazyTeamPerformanceSearchQuery,
  useSearchResourcesQuery,
} = searchApi;
